import { useState, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useDisclosure } from '@chakra-ui/react';
import AGGridMain from '@/components/tables/AGGrid/AGGridMain';
import useColumnState from '../../../hooks/useColumnState';
import CopyPermissionsSetModal from './CopyPermissionsSetModal';
import walkthroughIds from './walkthroughIds';
import { useNavigate } from 'react-router-dom';
import useCopyPermissionsSet from './queries/useCopyPermissionsSet';
import { canEditPermission } from './utils';
import { addSpacing } from '@/utils/utils';
import { REQUIRED_SCOPE_FILTER_PARAMS } from './constants';
import { useQuery } from 'hooks/useCustomQuery';
import { axios } from '@/services/axios';
import ActionMenu from 'components/presentational/ActionMenu';
import { EditIcon } from '@chakra-ui/icons';
import { CopyIcon } from 'components/v4/Icon/CopyIcon';

const Permissions = () => {
    const dispatch = useDispatch();
    const tableRef = useRef();
    const navigate = useNavigate();
    const [selectedPermissionsSet, setSelectedPermissionsSet] = useState(null);
    const [copyName, setCopyName] = useState(
        `Copy of ${selectedPermissionsSet?.name}`,
    );
    const { isOpen, onOpen, onClose } = useDisclosure();
    const finalRef = useRef(null);

    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });

    const { requiredScope: accountScope, currentScope } = useSelector(
        (state) => {
            return { ...state.login };
        },
    );

    const baseColumns = useMemo(
        () => [
            { headerName: 'Company', field: 'companyName', sortable: true },
            { headerName: 'Permission Set', field: 'name', sortable: true },
            {
                headerName: 'Required Scope',
                field: 'requiredScopeName',
                sortable: true,
                valueFormatter: (params) => addSpacing(params.value),
                filterParams: REQUIRED_SCOPE_FILTER_PARAMS,
            },
            {
                headerName: 'Read Only Permissions',
                field: 'readOnly',
                hide: true,
            },
            {
                headerName: 'Opt Out of New Permissions',
                field: 'optOutOfNew',
                hide: true,
            },
            {
                lockPosition: 'right',
                headerName: 'Action',
                maxWidth: 100,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                cellRenderer: function ActionsRenderer(params) {
                    const rowData = params.data;
                    const canEdit = canEditPermission(
                        rowData,
                        accountScope,
                        currentCompany,
                    );

                    const actionItems = [];

                    if (canEdit) {
                        actionItems.push({
                            label: 'Edit',
                            onClick: () => {
                                // Set the scope to match the row's requiredScope before navigating
                                if (
                                    rowData.requiredScope &&
                                    rowData.requiredScope !== currentScope
                                ) {
                                    dispatch({
                                        type: 'CHANGE_CURRENT_SCOPE',
                                        payload: rowData.requiredScope,
                                    });
                                }
                                // Keep scope dropdown enabled during navigation
                                window.__adminsPermissionsTabActive = true;
                                navigate(`permissions/${rowData.id}/edit`, {
                                    state: rowData,
                                });
                            },
                            icon: EditIcon,
                            dataAttributes: {
                                'data-walkthroughid': `${walkthroughIds.ufTableEditBtn}/${rowData?.name}`,
                            },
                        });
                    }

                    actionItems.push({
                        label: 'Copy',
                        onClick: () => {
                            setSelectedPermissionsSet(rowData);
                            onOpen();
                        },
                        icon: CopyIcon,
                    });

                    return <ActionMenu actionItems={actionItems} />;
                },
            },
        ],
        [
            accountScope,
            currentCompany,
            currentScope,
            dispatch,
            navigate,
            onOpen,
        ],
    );

    const { columns: agGridColumns, onColumnVisible } =
        useColumnState(baseColumns);

    // Data fetching with TanStack useQuery
    const {
        data: permissionsSets = [],
        isLoading,
        refetch,
    } = useQuery({
        queryKey: ['permissionsSets', currentScope],
        queryFn: async () => {
            const res = await axios.get(
                `/permissionsets/${currentScope}/${currentCompany}`,
            );
            dispatch({
                type: 'CHANGE_TABLE_LOADING',
            });
            return res.data || [];
        },
        cacheTime: 0,
        staleTime: 0,
    });

    // Copy functionality
    const { mutate: copyPermissionsSet, isLoading: isCopyingPermissionsSet } =
        useCopyPermissionsSet({
            permissionsSet: selectedPermissionsSet,
            currentCompany: currentCompany,
            navigate: navigate,
            copyName: copyName,
            accountScope: accountScope,
        });

    const tableDataWithColumns = useMemo(
        () => ({
            columns: agGridColumns,
            data: permissionsSets,
        }),
        [agGridColumns, permissionsSets],
    );

    const header = 'Permissions Sets';

    return (
        <>
            <AGGridMain
                gridRef={tableRef}
                tableData={tableDataWithColumns}
                titleName={header}
                isLoading={isLoading}
                refetch={refetch}
                onColumnVisible={onColumnVisible}
            />
            <CopyPermissionsSetModal
                isOpen={isOpen}
                onClose={onClose}
                data={selectedPermissionsSet}
                finalRef={finalRef}
                copyOnClick={copyPermissionsSet}
                copyName={copyName}
                setCopyName={setCopyName}
                isCopyingPermissionsSet={isCopyingPermissionsSet}
            />
        </>
    );
};

export default Permissions;
